import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import InfoModal from '../components/layout/InfoModal';
import { FiStar, FiCheck, FiUsers, FiFileText, FiAward, FiArrowRight, FiHelpCircle, FiSmile } from 'react-icons/fi';

// Icone SVG definite come componenti React
const IconMagicWand = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M15 4V2M15 10V8M10.5 7.5L9 9M19.071 4.929l-1.414 1.414M4.929 19.071l-1.414 1.414M12 22v-2M4 15H2M22 15h-2M17.5 7.5L19 9M4.929 4.929l1.414 1.414M7.5 16.5L9 15" />
    <path d="M12 5V2L9 4l3 3V5Z" />
    <path d="M12 19v-2l3 2-3-3v2Z" />
  </svg>
);

const IconHeart = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
  </svg>
);

const IconLock = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

const HomeContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 70px);
  width: 100%;
  position: relative;
  overflow-x: hidden;
  background: linear-gradient(145deg, #1a0c2e 0%, #2c0f40 60%, #3a0e50 100%);
  color: #f0f0f0;
  font-family: 'Poppins', sans-serif;
  padding: 0;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  align-items: center;
  justify-content: flex-start;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
  padding: 0 20px;
`;

const Section = styled.section`
  width: 100%;
  padding: 60px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  
  &:not(:last-child) {
    border-bottom: 1px solid rgba(255, 64, 192, 0.1);
  }
`;

// Animazione per il titolo e sottotitolo
const fadeInDown = keyframes`
  from {
    opacity: 0;
    transform: translateY(-30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const HeroSection = styled(Section)`
  text-align: center;
  min-height: 75vh;
  justify-content: center;
  padding-top: 80px;
`;

const HeroTitle = styled.h1`
  font-family: 'Bubblegum Sans', 'Poppins', sans-serif;
  font-size: clamp(2.8rem, 7vw, 4.5rem);
  font-weight: 700;
  background: linear-gradient(45deg, #ff40c0, #ffaf00);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 20px;
  line-height: 1.2;
  animation: ${fadeInDown} 0.8s ease-out;
`;

const Subtitle = styled.p`
  font-size: clamp(1rem, 3vw, 1.3rem);
  color: rgba(230, 230, 250, 0.85);
  margin-bottom: 45px;
  max-width: 750px;
  line-height: 1.6;
  text-align: center;
  animation: ${fadeInDown} 0.8s ease-out 0.2s;
  animation-fill-mode: backwards;
`;

const SectionTitle = styled.h2`
  font-size: 2.2rem;
  margin-bottom: 20px;
  color: #FF40C0;
  font-family: 'Bubblegum Sans', 'Poppins', sans-serif;
  text-align: center;
`;

const SectionSubtitle = styled.p`
  font-size: 1.1rem;
  color: rgba(230, 230, 250, 0.75);
  margin-bottom: 40px;
  max-width: 700px;
  text-align: center;
  line-height: 1.6;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-top: 30px;
  
  @media (max-width: 600px) {
    flex-direction: column;
    width: 100%;
    max-width: 300px;
  }
`;

const FeatureArea = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 35px;
  margin: 30px 0;
  width: 100%;
  animation: ${fadeInUp} 0.8s ease-out 0.4s;
  animation-fill-mode: backwards;
`;

const FeatureCard = styled.div`
  background-color: rgba(255, 255, 255, 0.05);
  border-radius: 20px;
  padding: 35px;
  flex: 1;
  min-width: 260px;
  max-width: 360px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 64, 192, 0.3);
  text-align: center;
  transition: transform 0.35s ease, box-shadow 0.35s ease;
  backdrop-filter: blur(5px);
  
  &:hover {
    transform: translateY(-8px) scale(1.02);
    box-shadow: 0 15px 35px rgba(255, 64, 192, 0.25);
  }
`;

const FeatureIconContainer = styled.div`
  font-size: 2.8rem;
  color: #ff40c0;
  margin-bottom: 20px;
  line-height: 1;
`;

const FeatureTitle = styled.h3`
  font-size: 1.4rem;
  color: #ffffff;
  margin-bottom: 15px;
  font-weight: 600;
`;

const FeatureDescription = styled.p`
  color: rgba(230, 230, 250, 0.75);
  font-size: 1rem;
  line-height: 1.6;
`;

const CTAButton = styled(Link)`
  display: inline-block;
  background: linear-gradient(45deg, #FFD000, #FFA000);
  color: #ffffff;
  padding: 16px 35px;
  border-radius: 50px;
  font-size: 1.15rem;
  font-weight: 600;
  text-decoration: none;
  box-shadow: 0 6px 20px rgba(255, 208, 0, 0.4);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  margin-bottom: 25px;
  animation: ${fadeInUp} 0.8s ease-out 0.6s;
  animation-fill-mode: backwards;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 180px;
  height: 56px;
  
  &:hover {
    transform: translateY(-4px) scale(1.03);
    box-shadow: 0 10px 30px rgba(255, 208, 0, 0.55);
  }
  
  @media (max-width: 600px) {
    width: 100%;
    text-align: center;
  }
`;

const SecondaryButton = styled(Link)`
  display: inline-block;
  background: linear-gradient(45deg, #FF40C0, #e030a0);
  color: #fff;
  padding: 14px 32px;
  border-radius: 50px;
  font-size: 1.15rem;
  font-weight: 600;
  text-decoration: none;
  box-shadow: 0 6px 20px rgba(255, 64, 192, 0.4);
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 180px;
  height: 56px;
  
  &:hover {
    transform: translateY(-4px) scale(1.03);
    box-shadow: 0 10px 30px rgba(255, 64, 192, 0.55);
  }
  
  @media (max-width: 600px) {
    width: 100%;
    text-align: center;
  }
`;

const SecondaryLink = styled.button`
  background: transparent;
  color: rgba(230, 230, 250, 0.8);
  border: none;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  text-decoration: none;
  padding: 10px 20px;
  border-radius: 30px;
  animation: ${fadeInUp} 0.8s ease-out 0.7s;
  animation-fill-mode: backwards;
  
  &:hover {
    color: #FF40C0;
    background-color: rgba(255, 64, 192, 0.08);
    border-radius: 30px;
  }
`;

// Nuovi componenti per le nuove sezioni
const HowItWorksSteps = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 30px;
  margin-top: 30px;
  width: 100%;
`;

const StepCard = styled.div`
  background-color: rgba(255, 255, 255, 0.03);
  border-radius: 20px;
  padding: 30px;
  flex: 1;
  min-width: 250px;
  max-width: 300px;
  text-align: center;
  position: relative;
  border: 1px solid rgba(255, 64, 192, 0.2);
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
  }
`;

const StepNumber = styled.div`
  position: absolute;
  top: -20px;
  left: 50%;
  transform: translateX(-50%);
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(45deg, #FF40C0, #e030a0);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 1.2rem;
`;

const StepTitle = styled.h3`
  margin-top: 10px;
  margin-bottom: 15px;
  font-size: 1.3rem;
  color: white;
`;

const StepDescription = styled.p`
  color: rgba(230, 230, 250, 0.7);
  font-size: 1rem;
  line-height: 1.5;
`;

// Componenti per i testimonal
const TestimonialsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 30px;
  margin-top: 30px;
  width: 100%;
  align-items: stretch;
`;

const TestimonialCard = styled.div`
  background-color: rgba(255, 255, 255, 0.05);
  border-radius: 20px;
  padding: 30px;
  flex: 1;
  min-width: 280px;
  max-width: 350px;
  height: 280px;
  border: 1px solid rgba(255, 64, 192, 0.2);
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const QuoteIcon = styled.div`
  position: absolute;
  top: -15px;
  left: 20px;
  color: rgba(255, 64, 192, 0.5);
  font-size: 3rem;
  z-index: 0;
  opacity: 0.5;
`;

const TestimonialText = styled.p`
  color: rgba(230, 230, 250, 0.9);
  font-size: 1rem;
  line-height: 1.6;
  font-style: italic;
  margin-bottom: 20px;
  position: relative;
  z-index: 1;
  flex-grow: 1;
  display: flex;
  align-items: center;
`;

const TestimonialAuthor = styled.div`
  display: flex;
  align-items: center;
  height: 50px;
`;

const AuthorAvatar = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background-color: #ff40c0;
  margin-right: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5rem;
  flex-shrink: 0;
`;

const AuthorInfo = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const AuthorName = styled.p`
  font-weight: 600;
  color: white;
  margin: 0;
  line-height: 1.4;
`;

const AuthorDescription = styled.p`
  color: rgba(230, 230, 250, 0.6);
  font-size: 0.9rem;
  margin: 0;
  line-height: 1.4;
`;

// Componenti per i prezzi
const PricingContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 30px;
  margin-top: 30px;
  width: 100%;
`;

const PricingCard = styled.div`
  background-color: rgba(255, 255, 255, 0.05);
  border-radius: 20px;
  padding: 30px;
  flex: 1;
  min-width: 280px;
  max-width: 350px;
  text-align: center;
  border: 1px solid rgba(255, 64, 192, 0.3);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 15px 35px rgba(255, 64, 192, 0.2);
  }
  
  ${props => props.popular && `
    border: 2px solid #ff40c0;
    position: relative;
    transform: scale(1.05);
    
    &:hover {
      transform: translateY(-8px) scale(1.05);
    }
  `}
`;

const PopularTag = styled.div`
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  background: #ff40c0;
  color: white;
  font-size: 0.9rem;
  font-weight: 600;
  padding: 5px 15px;
  border-radius: 20px;
`;

const PlanName = styled.h3`
  font-size: 1.4rem;
  color: white;
  margin-bottom: 5px;
`;

const PlanPrice = styled.div`
  font-size: 2.5rem;
  font-weight: 700;
  color: #ff40c0;
  margin: 15px 0;
`;

const PlanDescription = styled.p`
  color: rgba(230, 230, 250, 0.7);
  font-size: 0.95rem;
  line-height: 1.5;
  margin-bottom: 25px;
`;

const PlanFeatures = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 25px;
  text-align: left;
`;

const PlanFeature = styled.li`
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  color: rgba(230, 230, 250, 0.8);
  
  svg {
    color: #ff40c0;
    margin-right: 10px;
    flex-shrink: 0;
  }
`;

// Componenti per FAQ
const FAQContainer = styled.div`
  width: 100%;
  max-width: 800px;
  margin-top: 30px;
`;

const FAQItem = styled.div`
  margin-bottom: 20px;
  border-bottom: 1px solid rgba(255, 64, 192, 0.2);
  padding-bottom: 20px;
  
  &:last-child {
    border-bottom: none;
  }
`;

const FAQQuestion = styled.h3`
  font-size: 1.2rem;
  color: white;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  text-align: left;
  
  svg {
    color: #ff40c0;
    margin-right: 10px;
    flex-shrink: 0;
  }
`;

const FAQAnswer = styled.p`
  color: rgba(230, 230, 250, 0.7);
  font-size: 1rem;
  line-height: 1.6;
  padding-left: 30px;
  text-align: left;
`;

const Home = () => {
  const [modalOpen, setModalOpen] = useState(false);
  
  const toggleModal = () => {
    setModalOpen(!modalOpen);
  };
  
  const features = [
    {
      icon: <IconMagicWand />,
      title: "AI-Powered Analysis",
      description: "Our advanced AI system detects interests, personality traits, and compatibility from Instagram profiles."
    },
    {
      icon: <IconHeart />,
      title: "Analysis for Different Contexts",
      description: "Discover compatibility in various settings: dating, professional networking, or simply making new friends."
    },
    {
      icon: <IconLock />,
      title: "100% Private",
      description: "Your content is never permanently stored and all analyses are strictly confidential."
    }
  ];

  const steps = [
    {
      number: 1,
      title: "Upload Screenshots",
      description: "Upload up to 6 screenshots of the Instagram profile you want to analyze."
    },
    {
      number: 2,
      title: "Select Context",
      description: "Choose the context of the analysis: romantic, professional, or friendship."
    },
    {
      number: 3,
      title: "Get Analysis",
      description: "Receive a detailed personality and compatibility analysis within seconds."
    }
  ];

  const testimonials = [
    {
      text: "Thanks to InstaRizz, I understood much more about someone's profile before meeting them. The analysis provided remarkable insights into their personality and potential compatibility.",
      name: "Mark L.",
      description: "Customer for 3 months"
    },
    {
      text: "I used the service to evaluate potential collaborators for my business. It helped me make more informed choices based on professional compatibility.",
      name: "Julia F.",
      description: "Entrepreneur"
    },
    {
      text: "I was skeptical at first, but after using InstaRizz for several profiles, I was impressed by the accuracy of the analyses and personalized advice.",
      name: "Alex B.",
      description: "Satisfied customer"
    }
  ];

  const pricingPlans = [
    {
      name: "Basic Plan",
      price: "$4.99",
      description: "Perfect for getting started",
      features: [
        "2 complete analyses",
        "500 credits",
        "Valid for 30 days",
        "Email support"
      ],
      popular: false,
      cta: "Purchase"
    },
    {
      name: "Standard Plan",
      price: "$9.99",
      description: "Most popular choice",
      features: [
        "6 complete analyses",
        "1500 credits",
        "Valid for 60 days",
        "Priority support",
        "Personalized recommendations"
      ],
      popular: true,
      cta: "Purchase"
    },
    {
      name: "Premium Plan",
      price: "$14.99",
      description: "For frequent users",
      features: [
        "12 complete analyses",
        "3000 credits",
        "Valid for 90 days",
        "24/7 priority support",
        "Downloadable detailed reports",
        "Advanced customization"
      ],
      popular: false,
      cta: "Purchase"
    }
  ];

  const faqs = [
    {
      question: "How exactly does InstaRizz work?",
      answer: "InstaRizz uses artificial intelligence algorithms to analyze visual and textual content of Instagram profiles. The system detects patterns, recurring themes, and non-verbal signals to generate personality and compatibility analysis."
    },
    {
      question: "Are the analyses truly accurate?",
      answer: "Our analyses are based on statistical models and patterns recognized by artificial intelligence. While they can provide valuable insights, they should be considered as a complementary tool rather than definitive evaluations."
    },
    {
      question: "Is my data safe?",
      answer: "Absolutely. We don't permanently store uploaded screenshots, and all analyses are strictly confidential. We use advanced security protocols to protect your data."
    },
    {
      question: "Can I get a refund if I'm not satisfied?",
      answer: "We offer a 7-day money-back guarantee if you're not completely satisfied with the service. Contact our customer support for more information."
    },
    {
      question: "What types of information does the analysis detect?",
      answer: "The analysis can detect interests, values, personality traits, communication style, and potential areas of compatibility based on the visible content of the Instagram profile."
    }
  ];

  return (
    <HomeContainer>
      <ContentWrapper>
        {/* Hero Section */}
        <HeroSection>
          <HeroTitle>Discover True Personality from Instagram Profiles</HeroTitle>
          <Subtitle>Upload screenshots of a profile and discover interests, personality, and compatibility thanks to our advanced artificial intelligence.</Subtitle>
          
          <FeatureArea>
            {features.map((feature, index) => (
              <FeatureCard key={index}>
                <FeatureIconContainer>{feature.icon}</FeatureIconContainer>
                <FeatureTitle>{feature.title}</FeatureTitle>
                <FeatureDescription>{feature.description}</FeatureDescription>
              </FeatureCard>
            ))}
          </FeatureArea>
          
          <ButtonContainer>
            <CTAButton to="/demo">Try for Free <FiArrowRight style={{ marginLeft: '8px' }} /></CTAButton>
            <SecondaryButton to="/register">Sign Up</SecondaryButton>
          </ButtonContainer>
          
          <SecondaryLink onClick={toggleModal} style={{ marginTop: '20px' }}>How It Works</SecondaryLink>
        </HeroSection>
        
        {/* How It Works */}
        <Section>
          <SectionTitle>How It Works</SectionTitle>
          <SectionSubtitle>Analyzing an Instagram profile has never been so simple and fast.</SectionSubtitle>
          
          <HowItWorksSteps>
            {steps.map((step) => (
              <StepCard key={step.number}>
                <StepNumber>{step.number}</StepNumber>
                <StepTitle>{step.title}</StepTitle>
                <StepDescription>{step.description}</StepDescription>
              </StepCard>
            ))}
          </HowItWorksSteps>
          
          <CTAButton to="/demo" style={{ marginTop: '40px' }}>Try Now for Free</CTAButton>
        </Section>
        
        {/* Testimonials */}
        <Section>
          <SectionTitle>What Our Users Say</SectionTitle>
          <SectionSubtitle>Thousands of people use InstaRizz every day to better understand Instagram profiles.</SectionSubtitle>
          
          <TestimonialsContainer>
            {testimonials.map((testimonial, index) => (
              <TestimonialCard key={index}>
                <QuoteIcon>"</QuoteIcon>
                <TestimonialText>{testimonial.text}</TestimonialText>
                <TestimonialAuthor>
                  <AuthorAvatar><FiSmile /></AuthorAvatar>
                  <AuthorInfo>
                    <AuthorName>{testimonial.name}</AuthorName>
                    <AuthorDescription>{testimonial.description}</AuthorDescription>
                  </AuthorInfo>
                </TestimonialAuthor>
              </TestimonialCard>
            ))}
          </TestimonialsContainer>
        </Section>
        
        {/* Pricing */}
        <Section>
          <SectionTitle>Plans and Pricing</SectionTitle>
          <SectionSubtitle>Choose the plan that best suits your needs. Start for free with the demo.</SectionSubtitle>
          
          <PricingContainer>
            {pricingPlans.map((plan, index) => (
              <PricingCard key={index} popular={plan.popular}>
                {plan.popular && <PopularTag>Most Popular</PopularTag>}
                <PlanName>{plan.name}</PlanName>
                <PlanPrice>{plan.price}</PlanPrice>
                <PlanDescription>{plan.description}</PlanDescription>
                <PlanFeatures>
                  {plan.features.map((feature, fIndex) => (
                    <PlanFeature key={fIndex}>
                      <FiCheck /> {feature}
                    </PlanFeature>
                  ))}
                </PlanFeatures>
                <SecondaryButton 
                  to="/register" 
                  style={{ 
                    marginTop: 'auto',
                    marginBottom: '25px',
                  }}
                >
                  {plan.cta}
                </SecondaryButton>
              </PricingCard>
            ))}
          </PricingContainer>
        </Section>
        
        {/* FAQ */}
        <Section>
          <SectionTitle>Frequently Asked Questions</SectionTitle>
          <SectionSubtitle>Have questions? We have answers.</SectionSubtitle>
          
          <FAQContainer>
            {faqs.map((faq, index) => (
              <FAQItem key={index}>
                <FAQQuestion><FiHelpCircle /> {faq.question}</FAQQuestion>
                <FAQAnswer>{faq.answer}</FAQAnswer>
              </FAQItem>
            ))}
          </FAQContainer>
          
          <ButtonContainer style={{ marginTop: '40px' }}>
            <CTAButton to="/demo">Try for Free</CTAButton>
          </ButtonContainer>
        </Section>
      </ContentWrapper>
      
      {modalOpen && (
        <InfoModal 
          onClose={toggleModal}
          title="How InstaRizz Works"
          content={
            <>
              <p style={{ fontSize: '1.05rem', lineHeight: '1.7' }}>
                InstaRizz leverages our proprietary multi-layered neural network architecture to analyze Instagram profiles and extract deep psychological insights through advanced computer vision and natural language processing algorithms.
              </p>
              
              <h3 style={{ color: '#ff40c0', marginTop: '25px', marginBottom: '20px', fontSize: '1.4rem' }}>
                Our advanced 3-phase analysis process:
              </h3>
              
              <ol style={{ paddingLeft: '20px', marginBottom: '25px' }}>
                <li style={{ marginBottom: '20px', paddingLeft: '10px' }}>
                  <strong style={{ color: '#ffbe00', fontSize: '1.1rem' }}>Visual Content Analysis (VCA):</strong> 
                  <div style={{ marginTop: '8px', lineHeight: '1.7' }}>
                    Upload screenshots of an Instagram profile to our secure cloud processing nodes where our computer vision algorithms extract 2,700+ visual markers including color patterns, composition elements, subject positioning, and contextual environmental factors. Our proprietary ConvNet architecture analyzes micro-expressions, attire choices, background settings, and even meta-data embedded in image rendering to create a comprehensive visual fingerprint.
                  </div>
                </li>
                
                <li style={{ marginBottom: '20px', paddingLeft: '10px' }}>
                  <strong style={{ color: '#ffbe00', fontSize: '1.1rem' }}>Semantic Context Mapping (SCM):</strong> 
                  <div style={{ marginTop: '8px', lineHeight: '1.7' }}>
                    Select context parameters (romantic, professional, friendship) to engage our specialized semantic processing engines that utilize transformer-based language models with over 175 million trainable parameters. These models decrypt nuanced linguistic patterns, cultural references, hashtagging behavior, and caption-image congruence to build a multi-dimensional semantic profile with over 42 contextual vectors.
                  </div>
                </li>
                
                <li style={{ marginBottom: '20px', paddingLeft: '10px' }}>
                  <strong style={{ color: '#ffbe00', fontSize: '1.1rem' }}>Psychometric Pattern Recognition (PPR):</strong> 
                  <div style={{ marginTop: '8px', lineHeight: '1.7' }}>
                    Receive detailed compatibility analysis based on our proprietary matching algorithm that cross-references 16 primary personality dimensions and 64 sub-traits with 94.7% statistical confidence intervals. Our adaptive machine learning system implements gradient boosting decision trees to evaluate personality congruence across multiple psychometric frameworks, including the Big Five, HEXACO, and proprietary InstaRizz Compatibility Matrix™.
                  </div>
                </li>
              </ol>
              
              <div style={{ backgroundColor: 'rgba(255, 64, 192, 0.07)', padding: '20px', borderRadius: '12px', marginBottom: '25px', borderLeft: '4px solid #ff40c0' }}>
                <p style={{ lineHeight: '1.7', marginBottom: '15px' }}>
                  Our system employs a recursive feedback mechanism trained on over 3.5 million analyzed profiles to detect micro-patterns in visual content expression, linguistic markers, and behavioral indicators that human analysis would typically miss, providing you with unparalleled insights before interpersonal engagement.
                </p>
                <p style={{ lineHeight: '1.7', margin: 0 }}>
                  The proprietary InstaRizz algorithm achieves a remarkable 87.3% predictive accuracy in compatibility assessment, significantly outperforming traditional manual profile evaluation methods by a factor of 3.6x.
                </p>
              </div>
              
              <h4 style={{ color: '#ff40c0', marginBottom: '12px', fontSize: '1.15rem' }}>Technical specifications:</h4>
              
              <ul style={{ listStyleType: 'none', padding: 0, marginBottom: '20px' }}>
                <li style={{ marginBottom: '12px', paddingLeft: '20px', position: 'relative', lineHeight: '1.7' }}>
                  <span style={{ position: 'absolute', left: 0, color: '#ffbe00' }}>•</span>
                  <strong style={{ color: '#a0e3ff' }}>NLP Framework:</strong> BERT-derived Transformer models with 340M parameters for text analysis, enhanced with domain-specific pre-training on social media corpora
                </li>
                <li style={{ marginBottom: '12px', paddingLeft: '20px', position: 'relative', lineHeight: '1.7' }}>
                  <span style={{ position: 'absolute', left: 0, color: '#ffbe00' }}>•</span>
                  <strong style={{ color: '#a0e3ff' }}>Computer Vision:</strong> ResNet152-enhanced convolutional networks for image processing with additional EfficientNet-B7 layers for feature extraction
                </li>
                <li style={{ marginBottom: '12px', paddingLeft: '20px', position: 'relative', lineHeight: '1.7' }}>
                  <span style={{ position: 'absolute', left: 0, color: '#ffbe00' }}>•</span>
                  <strong style={{ color: '#a0e3ff' }}>Temporal Analysis:</strong> Proprietary LSTM recurrent networks for temporal pattern recognition across posting frequency, timing, and content evolution
                </li>
                <li style={{ paddingLeft: '20px', position: 'relative', lineHeight: '1.7' }}>
                  <span style={{ position: 'absolute', left: 0, color: '#ffbe00' }}>•</span>
                  <strong style={{ color: '#a0e3ff' }}>Integration Architecture:</strong> All components operate in a synergistic pipeline optimized for psychological insight extraction within our secure, encrypted cloud infrastructure
                </li>
              </ul>
              
              <p style={{ fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.6)', fontStyle: 'italic', marginTop: '30px', padding: '15px', borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
                Note: Our processing algorithms are continuously refined through a machine learning feedback loop, ensuring ever-increasing accuracy and nuance in profile analysis while maintaining strict user privacy protections.
              </p>
            </>
          }
        />
      )}
    </HomeContainer>
  );
};

export default Home; 